import { GraphQLFormattedError } from 'graphql'
import _omit from 'lodash/omit'
import _pick from 'lodash/pick'
import * as Yup from 'yup'
import { ApolloError, ApolloQueryResult } from '@apollo/client'
import { v7 as uuid } from 'uuid'
import { FieldTypeEnum, TableFieldTypeEnum, TableFieldEnumType } from "@/state/main-form/types"
import { GenericAnswer, isAnswerBoolean, isAnswerDate, isAnswerDecimal, isAnswerFile, isAnswerText } from "./types"
import { isValidUrl } from '@/utils/functions'
import { FieldType, InitialAnswerType, isTableField, TableFieldType } from '@/state/main-form/types'
import { RootState } from '@/state/redux-store'
import { selectAnswersForField, selectFormIsDirtyFromUserInteraction, selectInvalidAnswers, selectObjectIdMap } from './selectors'
import { AnswerContentTypeEnum } from '@/__gen/types'
import { SaveAnswersAuthMutation, SaveAnswersAuthMutationVariables } from './save-answers-auth/__gen'
import { SaveAnswersPublicMutation, SaveAnswersPublicMutationVariables } from './save-answers-public/__gen'
import { AnswerInState } from './reducer'

const ANSWER_TYPENAME_LOOKUP: {[k in TableFieldEnumType]: GenericAnswer['__typename']} = {
	[TableFieldTypeEnum.Address]: 'AnswerTextType',
	[TableFieldTypeEnum.Boolean]: 'AnswerBooleanType',
	[TableFieldTypeEnum.Date]: 'AnswerDateType',
	[TableFieldTypeEnum.Currency]: 'AnswerDecimalType',
	[TableFieldTypeEnum.DropDown]: 'AnswerTextType',
	[TableFieldTypeEnum.DropDownOther]: 'AnswerTextType',
	[TableFieldTypeEnum.Email]: 'AnswerTextType',
	[TableFieldTypeEnum.File]: 'AnswerFileType',
	[TableFieldTypeEnum.Image]: 'AnswerFileType',
	[TableFieldTypeEnum.Integer]: 'AnswerDecimalType',
	[TableFieldTypeEnum.Link]: 'AnswerTextType',
	[TableFieldTypeEnum.Radio]: 'AnswerTextType',
	[TableFieldTypeEnum.Rating]: 'AnswerDecimalType',
	[TableFieldTypeEnum.TextLong]: 'AnswerTextType',
	[TableFieldTypeEnum.TextShort]: 'AnswerTextType',
	[TableFieldTypeEnum.TextTiny]: 'AnswerTextType',
	[TableFieldTypeEnum.Time]: 'AnswerDateType',
}

const ANSWER_VALIDATORS: {[k in TableFieldEnumType]: null | ((a: GenericAnswer) => boolean)} = {
	[TableFieldTypeEnum.Address]: null,
	[TableFieldTypeEnum.Boolean]: null,
	[TableFieldTypeEnum.Date]: null,
	[TableFieldTypeEnum.Currency]: null,
	[TableFieldTypeEnum.DropDown]: null,
	[TableFieldTypeEnum.DropDownOther]: null,
	[TableFieldTypeEnum.Email]: (a: GenericAnswer) => !a || !a.content || Yup.string().email().nullable().isValidSync(a.content),
	[TableFieldTypeEnum.File]: null,
	[TableFieldTypeEnum.Image]: null,
	[TableFieldTypeEnum.Integer]: null,
	[TableFieldTypeEnum.Link]: (a: GenericAnswer) => !a || !a.content || isValidUrl(a.content),
	[TableFieldTypeEnum.Radio]: null,
	[TableFieldTypeEnum.Rating]: null,
	[TableFieldTypeEnum.TextLong]: null,
	[TableFieldTypeEnum.TextShort]: null,
	[TableFieldTypeEnum.TextTiny]: null,
	[TableFieldTypeEnum.Time]: null,
}

export const isAnswerValid = (fieldType: TableFieldEnumType, a: GenericAnswer) => {
	const b = fieldType as TableFieldEnumType
	if (!a.content || !(fieldType in ANSWER_VALIDATORS)) {
		return true
	}

	const validator = ANSWER_VALIDATORS[fieldType]
	return validator ? validator(a) : true
	
}

export const getRanksFromAnswers = (answers: GenericAnswer[]) => (
	Array.from(new Set(answers.map(a => a.rank))).sort((a,b)=>a-b)
)


export const createNewAnswer = ({field, tableField, objectIdMap, initialAnswers, rank}:{
	objectIdMap: {[k in AnswerContentTypeEnum]: string},
	field: FieldType,
	tableField: TableFieldType | null,
	rank: number,
	initialAnswers?: {[rank: number]: InitialAnswerType[]}
}) => {
	if (field.type == FieldTypeEnum.Table && !tableField) {
		const message = "createAnswer must receive tableField for a table"
		alert(message)
		throw Error(message)
	}

	const commonAttributes = {
		id: uuid(),
		fieldId: field.id,
		tableFieldId: tableField?.id || null,
		rank: rank,
		new: true,
		contentType: field.contentType,
		objectId: objectIdMap[field.contentType],
		deactivated: null,
	}

	const __typename = ANSWER_TYPENAME_LOOKUP[(field.type == FieldTypeEnum.Table ? tableField!.type : field.type)];
	let answer: GenericAnswer

	const initialAnswer: InitialAnswerType | undefined = initialAnswers && initialAnswers[rank] && initialAnswers[rank].find(
		(i: InitialAnswerType) => tableField ? i.tableFieldId == tableField.id : i.fieldId == field.id
	)
	
	if (__typename == 'AnswerFileType') {
		answer = {
			__typename,
			content: initialAnswer ? initialAnswer.contentFile : null,
			name: initialAnswer ? initialAnswer.contentFileName : null,
			...commonAttributes,
		}
	} else if (__typename == 'AnswerBooleanType') {
		answer = {
			__typename,
			content: initialAnswer ? initialAnswer.contentBoolean : false,
			...commonAttributes,
		}
	} else if (__typename == 'AnswerDateType') {
		answer = {
			__typename,
			content: initialAnswer ? initialAnswer.contentDate : null,
			...commonAttributes,
		}
	} else if (__typename == 'AnswerDecimalType') {
		answer = {
			__typename,
			content:  initialAnswer ? initialAnswer.contentDecimal : null,
			...commonAttributes,
		}
	} else if (__typename == 'AnswerTextType') {
		answer = {
			__typename,
			content:  initialAnswer ? initialAnswer.contentText : null,
			...commonAttributes,
		}
	} else {
		throw Error('Unable to infer type')
	}
	return answer
}

export const createNewAnswerRow = ({mainField, objectIdMap, initialAnswers, rank}:{
	objectIdMap: {[k in AnswerContentTypeEnum]: string},
	mainField: FieldType,
	rank: number,
	initialAnswers?: {[rank: number]: InitialAnswerType[]}
}) => {
	const answers = []
	const fields = mainField.type == FieldTypeEnum.Table ? mainField.tableFields : [mainField]

	for(const fOrTf of fields) {
		answers.push(createNewAnswer({
			field: mainField,
			tableField: isTableField(fOrTf) ? fOrTf : null,
			objectIdMap,
			rank,
			initialAnswers,
		}))
	}
	return answers
}


export const createMissingAnswers = ({state, objectIdMap}: {state: RootState, objectIdMap: ReturnType<typeof selectObjectIdMap>}) => {
	if (!state.mainForm.data) {throw Error('missing mainForm.data')}
	const toCreate = []
	const tables = Object.values(state.mainForm.data.fields).filter(field => field.type == FieldTypeEnum.Table)
	for (const field of tables) {
		const answers = selectAnswersForField(state, field.id)
		const ranks = getRanksFromAnswers(answers)
		for (const rank of ranks) {
			for (const tableField of field.tableFields) {
				const answer = Object.values(state.answers.objects).find(a => a.rank == rank && a.tableFieldId == tableField.id)
				if (!answer) {
					toCreate.push(createNewAnswer({
						objectIdMap,
						field,
						tableField,
						rank,
					}))
				}
			}
		}
	}
	return toCreate
}

export const getSaveAnswerVariables = (state: RootState) => {
	if (!state.mainForm.data) {
		alert('Unable to save answers. Form not loaded.')
		throw Error('Unable to save answers. Form not loaded.')
	}

	const toSave = Object.keys(state.answers.status.dirtyInFlight).map(id => {
		return state.answers.objects[id]
	})

	const variables: SaveAnswersAuthMutationVariables | SaveAnswersPublicMutationVariables = {
		formSave: state.answers.session.saved ? [] : [{
			id: state.answers.session.id,
			record: state.mainForm.data.record.id,
			form: state.mainForm.data.form.id,
			publicForm: state.mainForm.data.publicForm?.id || null,
		}],
		answerText: [],
		answerDecimal: [],
		answerDate: [],
		answerBoolean: [],
		answerFile: [],
	}

	const transformForSave = (answer: AnswerInState) => {
		let updateParams = {
				id: answer.id,
				deactivated: answer.deactivated,
				rank: answer.rank,
		}
		return answer.newInFlight ? {
				...updateParams,
				id: answer.id,
				content: answer.content,
				fieldId: answer.fieldId,
				tableFieldId: answer.tableFieldId,
				contentType: answer.contentType,
				objectId: answer.objectId,
				rank: answer.rank,
				deactivated: answer.deactivated,
				formSaveId: state.answers.session.id,
				replacingId: answer.replacingId,
				...('name' in answer ? {name: answer.name} : {}),
				...('lat' in answer ? {lat: answer.lat} : {}),
				...('lng' in answer ? {lng: answer.lng} : {}),
			} : updateParams
	}

	for (const answer of toSave) {
		if (isAnswerText(answer)) {
			variables.answerText.push(transformForSave(answer))
		} else if (isAnswerDecimal(answer)) {
			variables.answerDecimal.push(transformForSave(answer))
		} else if (isAnswerDate(answer)) {
			variables.answerDate.push(transformForSave(answer))
		} else if (isAnswerBoolean(answer)) {
			variables.answerBoolean.push(transformForSave(answer))
		} else if (isAnswerFile(answer)) {
			variables.answerFile.push(transformForSave(answer))
		} else {
			alert('Error saving answers')
			throw Error(`Unknown answer type`)
		}
	}
	return variables
}

const handleSignatureErrors = (errors: readonly (GraphQLFormattedError | ApolloError)[] | undefined) => {
	const scopedError = errors && errors[0]
	const key = 'cant_edit_values_due_to_signatures:'
	if (scopedError && scopedError.message.startsWith(key)) {
		const name = scopedError.message.replace(key, "")
		const message = [
			`This form cannot be saved due to signatures on:`,
			`\u00B7 ${name}`,
			`If this isn't the current form, they may share the same field. Remove the actions on the above form to make edits.`
		].join('\n\n')
		alert(message)
		throw Error(scopedError.toString())
	}
}

export const extractSaveAnswerPubResponse = ({data, errors, error}: ApolloQueryResult<SaveAnswersPublicMutation>) => {
	handleSignatureErrors(errors)
	
	if (
		error
		|| (errors && errors.length)
		|| !data.answerTextPub_BulkUpdate?.objects
		|| !data.answerDecimalPub_BulkUpdate?.objects
		|| !data.answerDatePub_BulkUpdate?.objects
		|| !data.answerBooleanPub_BulkUpdate?.objects
		|| !data.answerFilePub_BulkUpdate?.objects
	) {
		const objErrors = [...Object.values(data).map(d => d && typeof d === 'object' && 'errors' in d && d.errors), errors, error]
		console.error('Error saving answers', [...objErrors, errors, error].filter(d => d))
		alert('There was an error saving your answers. If this issue persists, please contact support.')
		throw Error(`Error extracting answers pub ${error} ${errors}`)
	}
	return [
		...data.answerTextPub_BulkUpdate.objects,
		...data.answerDecimalPub_BulkUpdate.objects,
		...data.answerDatePub_BulkUpdate.objects,
		...data.answerBooleanPub_BulkUpdate.objects,
		...data.answerFilePub_BulkUpdate.objects,
	]
}

export const extractSaveAnswerResponse = ({data, errors, error}: ApolloQueryResult<SaveAnswersAuthMutation>) => {
	handleSignatureErrors(errors)
	
	if (
		error
		|| (errors && errors.length)
		|| !data.answerText_BulkUpdate?.objects
		|| !data.answerDecimal_BulkUpdate?.objects
		|| !data.answerDate_BulkUpdate?.objects
		|| !data.answerBoolean_BulkUpdate?.objects
		|| !data.answerFile_BulkUpdate?.objects
	) {
		const objErrors = [...Object.values(data).map(d => d && typeof d === 'object' && 'errors' in d && d.errors), errors, error]
		console.error('Error saving answers', [...objErrors, errors, error].filter(d => d))
		alert('There was an error saving your answers. If this issue persists, please contact support.')
		throw Error(`Error extracting answers auth ${error} ${errors}`)
	}
	return [
		...data.answerText_BulkUpdate.objects,
		...data.answerDecimal_BulkUpdate.objects,
		...data.answerDate_BulkUpdate.objects,
		...data.answerBoolean_BulkUpdate.objects,
		...data.answerFile_BulkUpdate.objects,
	]
}

const MAX_WAIT = 5000 // milliseconds
const POLL_INTERVAL = 200 // millisecond
export const waitForSave = async (getState: () => RootState) => {
	for (var i=0;i<=MAX_WAIT; i+= POLL_INTERVAL) {
		const state = getState()
		if (selectFormIsDirtyFromUserInteraction(state) || state.answers.saving || state.answers.queuedSave) {
			console.log(`Waiting for save: ${i} / ${MAX_WAIT}ms`)
			await new Promise<void>((resolve, reject) => {
				setTimeout(async () => {
					resolve()
				}, POLL_INTERVAL)
			})
		} else {
			return { success: true}
		}
	}
	console.error("Stuck waiting for save")
	alert("It seems there was an issue waiting for the form to save. If this issue persists, please contact support")
	return { success: false }
}

export const addBlockNavigationListener = (getState: () => RootState) => {
	window.addEventListener("beforeunload", function(event) {
		const state = getState()
		if (selectFormIsDirtyFromUserInteraction(state)) {
			event.preventDefault();
			return event.returnValue = "Your changes are still saving. Are you sure you want to leave?";
		}
		return false
	})
}